import { HostAriaLabelMixin, SchmancyElement } from '@schmancy/mixins/index'
import { css, html, LitElement } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { BehaviorSubject, combineLatest } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import { type ControlSize, sizeClasses } from '@schmancy/theme/control-size'
import { type SchmancyTone, type ToneVariant, toneColors } from '@schmancy/theme/tone'
import '@schmancy/primitives/icons/icon'

/** Assist chip — prompts a user action (open a calendar event, share content). */
@customElement('schmancy-assist-chip')
export class SchmancyAssistChip extends HostAriaLabelMixin(SchmancyElement) {
	static styles = [
		css`
			:host {
				display: inline-block;
				outline: none;
				width: fit-content;
				min-width: fit-content;
			}

			:host([disabled]) {
				pointer-events: none;
			}

			.ripple {
				position: absolute;
				border-radius: var(--schmancy-radius-circle, 50%);
				transform: scale(0);
				animation: ripple 600ms linear;
				background-color: rgba(0, 0, 0, 0.08);
				pointer-events: none;
			}

			@keyframes ripple {
				to {
					transform: scale(4);
					opacity: 0;
				}
			}

			/* State layer for M3 hover/focus/pressed states */
			.state-layer {
				position: absolute;
				inset: 0;
				border-radius: inherit;
				pointer-events: none;
				background-color: currentColor;
				opacity: 0;
				transition: opacity 200ms ease;
			}

			:host(:not([disabled])) button:hover .state-layer {
				opacity: 0.08;
			}

			:host(:not([disabled])) button:focus-visible .state-layer {
				opacity: 0.1;
			}

			:host(:not([disabled])) button:active .state-layer {
				opacity: 0.1;
			}
		`,
	]
	@property({ reflect: true }) value = ''

	/** Material Symbols icon name. */
	@property({ reflect: true }) icon = ''

	@property({ reflect: true }) href = ''

	@property({ reflect: true }) target = ''

	@property({ type: Boolean, reflect: true }) disabled = false

	/** Elevated by default per M3 spec for assist chips. */
	@property({ type: Boolean, reflect: true }) elevated = true

	/** Size from the shared control-size ladder. Default 'sm' = 40px / 14px. */
	@property({ reflect: true }) size: ControlSize = 'sm'

	/**
	 * The action mark of the tone family — the one member of the family that
	 * is clickable. Unset (the default) keeps the elevated-neutral treatment
	 * every assist chip has always carried, so no existing chip moves; set to
	 * a tone and the chip becomes a tonally-marked micro-action — a revert, an
	 * undo, an "edited" affordance — carrying that tone's hue.
	 *
	 * `color` picks the hue, `variant` picks how hard the chip presses it. They
	 * are two properties because one tone has to serve both a chip that owns
	 * its row and a chip annotating content it must not outrank. The M3 state
	 * layer paints from `currentColor`, so hover/focus/pressed follow the tone
	 * at every rung with nothing further to declare.
	 */
	@property({ reflect: true }) color?: SchmancyTone

	/**
	 * How hard the chip presses its tone, on the same four-rung ladder
	 * `<schmancy-tone-dot>` and `<schmancy-tone-spine>` sit on. Ignored while
	 * `color` is unset — an untoned chip has no tone to press.
	 *
	 * `soft` is the default: a tinted ground reads as a control at a glance,
	 * where `solid` on something this small reads as a `<schmancy-button>`
	 * that lost its size.
	 *
	 * `outline` is the rung for a chip that annotates rather than announces —
	 * an "edited" marker beside a title, a footnote-scale revert. Its ground
	 * is transparent, so the chip takes whatever surface it was dropped onto
	 * and its hairline is the only boundary: still visibly a control, without
	 * matching the heading it sits next to for weight. Pair it with
	 * `elevated=false` — a shadow under a transparent ground reads as a raised
	 * pane of glass.
	 */
	@property({ reflect: true }) variant: ToneVariant = 'soft'

	private hover$ = new BehaviorSubject<boolean>(false)
	private pressed$ = new BehaviorSubject<boolean>(false)
	private focused$ = new BehaviorSubject<boolean>(false)

	// Only ripples drive rendering; hover/pressed/focused stay outside @state.
	@state() private ripples: Array<{ x: number; y: number; id: number }> = []

	protected static shadowRootOptions = { ...LitElement.shadowRootOptions, delegatesFocus: true }

	private nextRippleId = 0

	connectedCallback() {
		super.connectedCallback()

		combineLatest([this.hover$, this.pressed$, this.focused$]).pipe(takeUntil(this.disconnecting)).subscribe()
	}

	private handleClick = (e: MouseEvent) => {
		if (this.disabled) return

		const button = this.shadowRoot?.querySelector('button')
		if (button) {
			const rect = button.getBoundingClientRect()
			const x = e.clientX - rect.left
			const y = e.clientY - rect.top
			const id = this.nextRippleId++

			this.ripples = [...this.ripples, { x, y, id }]

			setTimeout(() => {
				this.ripples = this.ripples.filter(r => r.id !== id)
			}, 600)
		}

		if (this.href) {
			if (this.target === '_blank') {
				window.open(this.href, '_blank')
			} else {
				window.location.href = this.href
			}
		}

		this.dispatchEvent(
			new CustomEvent('action', {
				detail: { value: this.value },
				bubbles: true,
				composed: true,
			}),
		)
	}

	private handleKeyDown = (e: KeyboardEvent) => {
		if (this.disabled) return

		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault()
			this.pressed$.next(true)

			const clickEvent = new MouseEvent('click', {
				bubbles: true,
				cancelable: true,
				clientX: 0,
				clientY: 0,
			})
			this.handleClick(clickEvent)

			setTimeout(() => this.pressed$.next(false), 100)
		}
	}

	private handleFocus = () => {
		this.focused$.next(true)
	}

	private handleBlur = () => {
		this.focused$.next(false)
	}

	render() {
		// styleMap drops the declarations it stops emitting, so a chip returned
		// to its untoned state loses the paint with it.
		const toned = this.color ? toneColors(this.color, this.variant) : undefined

		const classes = {
			relative: true,
			'inline-flex': true,
			'items-center': true,
			'rounded-full': true,
			'cursor-pointer': !this.disabled,
			'transition-colors': true,
			'duration-200': true,
			'select-none': true,
			'overflow-hidden': true,

			// Size ladder: height / font / horizontal-pad / gap from control-size
			[sizeClasses(this.size, 'chip')]: true,

			'bg-surface-containerLow': !this.color,
			'text-surface-onVariant': !this.color,

			// toneColors carries the hairline's colour only; with no width and
			// style of its own the rung would render no edge at all. Two rungs are
			// given one, so the edge follows the map rather than a rung's name.
			'border border-solid': toned?.borderColor !== undefined,

			'shadow-sm': this.elevated && !this.disabled, // M3 elevation level 1
			'hover:shadow-md': this.elevated && !this.disabled,

			'focus-visible:outline-solid': !this.disabled,
			'focus-visible:outline-2': !this.disabled,
			'focus-visible:outline-primary': !this.disabled,
			'focus-visible:outline-offset-2': !this.disabled,

			'opacity-38': this.disabled, // M3 disabled opacity
			'cursor-not-allowed': this.disabled,
		}

		return html`
			<button
				type="button"
				class=${this.classMap(classes)}
				style=${this.styleMap(toned ?? {})}
				?disabled=${this.disabled}
				@click=${this.handleClick}
				@keydown=${this.handleKeyDown}
				@mouseenter=${() => this.hover$.next(true)}
				@mouseleave=${() => this.hover$.next(false)}
				@mousedown=${() => this.pressed$.next(true)}
				@mouseup=${() => this.pressed$.next(false)}
				@focus=${this.handleFocus}
				@blur=${this.handleBlur}
				tabindex=${this.disabled ? '-1' : '0'}
				role="button"
				aria-disabled=${this.disabled}
				aria-label=${ifDefined(this.ariaLabel || undefined)}
			>
				${this.icon ? html` <schmancy-icon class="text-[18px] shrink-0">${this.icon}</schmancy-icon> ` : ''}
				<span class="font-medium">
					<slot></slot>
				</span>

				${this.ripples.map(
					ripple => html` <span class="ripple" style="left: ${ripple.x}px; top: ${ripple.y}px;"></span> `,
				)}

				<div class="state-layer"></div>
			</button>
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-assist-chip': SchmancyAssistChip
	}
}

export type AssistChipActionEvent = { value: string }
